/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Badge } from '@hashicorp/mds-react/components'
import sendPosthogEvent from '@web/utils/send-posthog-event'
import s from './style.module.css'

export interface IExpandableTags {
	tags: ILinkTag[]
}

export interface ILinkTag {
	src: string
	text: string
	slug: string
	icon?: {
		name: string
		color: string
	}
}

const UNEXPANDED_NUM_OF_TAGS = 2

export const ExpandableTags = ({ tags }: IExpandableTags) => {
	// This ensures that we only control the user's focus if they click the `expander` button
	const [handleFocus, setHandleFocus] = useState<boolean>(false)
	const [showExpander, setShowExpander] = useState<boolean>(false)
	const [isExpanded, setIsExpanded] = useState<boolean>(false)
	const listItemRefs = useRef<[] | HTMLAnchorElement[]>([])

	const handleExpanderClick = () => {
		// We only set this to `true` if the expander button is clicked.
		setHandleFocus(true)
		setIsExpanded((state) => !state)
	}

	// Show expander tag button if there are more than two tags
	useEffect(() => {
		const shouldShowExpander = tags && tags.length > UNEXPANDED_NUM_OF_TAGS
		if (shouldShowExpander) {
			setShowExpander(true)
		}
	}, [tags, setShowExpander, isExpanded])

	// a11y
	// When tags are expanded, focus should be set on the next newly visible tag.
	// Since the second tag is the last visible tag, this means that the focus should be
	// set on the third tag (index 2).
	// When tags are hidden, focus should be set on the last visible tag.
	// This means that the focus should be set on the second tag (index 1).
	useEffect(() => {
		const shouldShowExpander = tags && tags.length > UNEXPANDED_NUM_OF_TAGS
		if (!shouldShowExpander || !handleFocus) return
		if (isExpanded && tags && tags.length) {
			if (listItemRefs?.current[2]) {
				listItemRefs?.current[2].focus()
			}
		} else {
			if (listItemRefs?.current[1]) {
				listItemRefs?.current[1].focus()
			}
		}
	}, [isExpanded, tags])

	return (
		<ul className={s.root}>
			{tags && tags.length
				? tags.map((tag, index) => {
						// When tags are not expanded, only make two tags visible
						// Maximum number of visible tags is six
						const isVisible = (index < 2 && !isExpanded) || isExpanded
						return (
							<li
								key={index}
								className={classNames([s.tag, { [s.isVisible]: isVisible }])}
								aria-hidden={isVisible ? 'false' : 'true'}
							>
								<a
									href={tag.src}
									className={s.link}
									ref={(el: HTMLAnchorElement) => {
										listItemRefs.current[index] = el
									}}
									onClick={() => {
										sendPosthogEvent('Blog Tag Clicked', {
											tag_slug: tag.slug,
										})
									}}
								>
									<Badge
										className={s.badge}
										text={tag.text}
										type="outlined"
										size="large"
										icon={tag.icon?.name}
										iconColor={tag.icon?.color}
									/>
								</a>
							</li>
						)
					})
				: null}
			{showExpander ? (
				<li>
					<button
						className={s.expandButton}
						onClick={handleExpanderClick}
						aria-label={isExpanded ? 'Hide tags' : 'Expand tags'}
					>
						<Badge
							accessibleText={isExpanded ? 'Close' : 'Expand'}
							icon={isExpanded ? 'x' : 'more-horizontal'}
							type="outlined"
							size="large"
						/>
					</button>
				</li>
			) : null}
		</ul>
	)
}
